import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function BonusCapture({ variant = "block" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/bonus/request", { email });
      setSent(true);
      toast.success("Sent — check your inbox in a minute.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`relative overflow-hidden ${variant === "block" ? "bg-[#2A3B32] text-[#F9F8F6]" : "bg-[#F9F8F6] border border-[#E5E2DC]"} p-8 sm:p-12`}>
      <div className="grid lg:grid-cols-12 gap-8 items-center relative z-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <Gift size={18} className={variant === "block" ? "text-[#B85C38]" : "text-[#B85C38]"} />
            <p className="eyebrow" style={variant === "block" ? { color: "#B85C38" } : {}}>Free companion workbook</p>
          </div>
          <h3 className={`font-serif-display text-3xl sm:text-4xl leading-none tracking-tight mt-4 ${variant === "block" ? "text-[#F9F8F6]" : ""}`}>
            $19 value.<br /><em className="italic">Yours, free.</em>
          </h3>
          <p className={`mt-4 max-w-lg leading-relaxed ${variant === "block" ? "text-[#F9F8F6]/70" : "text-[#5C5C5C]"}`}>
            Two short PDFs — the <em>Discipline Tracker</em> for weekly reflection, and the <em>Transformation Guide</em> with a real week-by-week example. Designed to be used alongside the book, but useful on their own.
          </p>
        </div>
        <div className="lg:col-span-5">
          {sent ? (
            <div className="border border-[#B85C38]/40 p-6 space-y-3" data-testid="bonus-sent-state">
              <p className="font-serif-display text-2xl">Yours to keep.</p>
              <p className={`text-sm ${variant === "block" ? "text-[#F9F8F6]/70" : "text-[#5C5C5C]"}`}>Download both PDFs below — a copy is also on the way to your inbox.</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <a
                  href="https://customer-assets.emergentagent.com/job_digital-library-217/artifacts/wragnul1_DISCIPLINE%20tracker.pdf"
                  target="_blank" rel="noreferrer"
                  data-testid="bonus-download-tracker"
                  className="inline-flex items-center px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase font-medium bg-[#B85C38] hover:bg-[#9C4A2A] text-white transition-colors"
                >
                  Discipline Tracker
                </a>
                <a
                  href="https://customer-assets.emergentagent.com/job_digital-library-217/artifacts/7bgfnf8y_TRANSFORMATION%20guide.pdf"
                  target="_blank" rel="noreferrer"
                  data-testid="bonus-download-guide"
                  className={`inline-flex items-center px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase font-medium transition-colors ${variant === "block" ? "bg-[#F9F8F6] text-[#2A3B32] hover:bg-white" : "bg-[#2A3B32] hover:bg-[#1C2A22] text-white"}`}
                >
                  Transformation Guide
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.com"
                data-testid="bonus-email"
                className={`w-full px-4 py-3.5 text-sm outline-none border ${variant === "block"
                  ? "bg-[#F9F8F6]/10 border-[#F9F8F6]/20 text-[#F9F8F6] placeholder:text-[#F9F8F6]/50 focus:border-[#F9F8F6]"
                  : "bg-white border-[#E5E2DC] text-[#1C1C1C] focus:border-[#2A3B32]"
                }`}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="bonus-submit"
                className={`inline-flex items-center justify-center px-6 py-3.5 text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${variant === "block"
                  ? "bg-[#B85C38] hover:bg-[#9C4A2A] text-white"
                  : "bg-[#2A3B32] hover:bg-[#1C2A22] text-white"
                }`}
              >
                {loading ? "Sending…" : "Send me the workbook"}
              </button>
              <p className={`text-[11px] ${variant === "block" ? "text-[#F9F8F6]/50" : "text-[#5C5C5C]"}`}>
                No spam. Unsubscribe with one click.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
